import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from "axios";
import type { ApiError } from "@/types/api";

export type ApiRequestOptions = AxiosRequestConfig & {
  retryUnsafeMethods?: boolean;
};

type RetryableRequestConfig = ApiRequestOptions & {
  _retryCount?: number;
};

export const getApiBaseUrl = (): string => {
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  if (!isLocal) {
    return ""; // Use same-origin rewrites so browsers do not call Railway directly.
  }
  const envApiUrl = import.meta.env.VITE_API_URL;
  if (envApiUrl) {
    // Trim whitespace/newlines that may have been accidentally added to env var
    return envApiUrl.trim();
  }
  return ""; // Use relative paths with Vite proxy
};

const USER_FRIENDLY_MESSAGES: Record<number, string> = {
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to perform this action.",
  404: "The requested resource was not found.",
  429: "Too many requests. Please wait a moment and try again.",
  503: "The service is starting up or temporarily unavailable. Please try again shortly.",
  500: "Something went wrong. Please try again or contact support.",
};

// Retry configuration for transient failures (503 from Railway proxy, network blips)
const MAX_RETRIES = 4;
const INITIAL_RETRY_DELAY_MS = 1000;
const RETRYABLE_METHODS = new Set(["get", "head", "options"]);

const isRetryableError = (error: AxiosError): boolean => {
  // Retry on network errors (ECONNREFUSED, etc.) or 502/503/504
  if (!error.response) return true; // Network error
  const status = error.response.status;
  const serviceState = (error.response.data as { status?: unknown } | undefined)
    ?.status;
  if (status === 503 && serviceState === "db_failed") return false;
  return status === 502 || status === 503 || status === 504;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function getRequestMethod(config: AxiosRequestConfig | undefined): string {
  return (config?.method || "get").toLowerCase();
}

function canRetryRequest(
  config: AxiosRequestConfig | undefined,
  error: AxiosError,
): boolean {
  if (!config || !isRetryableError(error)) return false;

  const method = getRequestMethod(config);
  return (
    RETRYABLE_METHODS.has(method) ||
    Boolean((config as RetryableRequestConfig).retryUnsafeMethods)
  );
}

function formatValidationIssue(issue: unknown): string | null {
  if (!issue || typeof issue !== "object") return null;

  const record = issue as {
    loc?: unknown;
    msg?: unknown;
  };

  if (typeof record.msg !== "string") return null;

  const path = Array.isArray(record.loc)
    ? record.loc
        .filter(
          (part): part is string | number =>
            typeof part === "string" || typeof part === "number",
        )
        .join(".")
    : "";

  return path ? `${path}: ${record.msg}` : record.msg;
}

function normalizeApiErrorDetail(detail: unknown): string | undefined {
  if (typeof detail === "string") {
    const normalized = detail.trim();
    return normalized || undefined;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map(formatValidationIssue)
      .filter((message): message is string => Boolean(message));

    if (messages.length > 0) {
      return messages.join(" ");
    }
  }

  if (detail && typeof detail === "object") {
    const nestedDetail = (detail as { detail?: unknown }).detail;
    if (nestedDetail !== undefined) {
      return normalizeApiErrorDetail(nestedDetail);
    }

    const issueMessage = formatValidationIssue(detail);
    if (issueMessage) {
      return issueMessage;
    }

    try {
      return JSON.stringify(detail);
    } catch {
      return undefined;
    }
  }

  return undefined;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true, // Critical for httpOnly cookies
});

// Bearer token holder for cross-domain auth
// (third-party cookies are blocked in modern browsers, so we use Authorization header)
let _authToken: string | null = null;
export const setClientAuthToken = (token: string | null) => {
  _authToken = token;
};
export const getClientAuthToken = () => _authToken;

// Request interceptor: attach Bearer token
apiClient.interceptors.request.use((config) => {
  if (_authToken) {
    config.headers.Authorization = `Bearer ${_authToken}`;
  }
  return config;
});

// Response interceptor for error handling and retries
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ detail?: unknown }>) => {
    const config = error.config;

    // Retry with exponential backoff for transient failures (Railway 503, cold starts)
    if (canRetryRequest(config, error)) {
      const retryConfig = config as RetryableRequestConfig;
      const retryCount = retryConfig._retryCount ?? 0;

      if (retryCount < MAX_RETRIES) {
        retryConfig._retryCount = retryCount + 1;
        const backoff = INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCount); // 1s, 2s, 4s, 8s
        await delay(backoff);
        return apiClient.request(retryConfig);
      }
    }

    const status = error.response?.status ?? 500;
    const serverDetail = normalizeApiErrorDetail(error.response?.data?.detail);
    const message =
      serverDetail || USER_FRIENDLY_MESSAGES[status] || error.message;

    const apiError: ApiError = {
      detail: message,
      status,
    };

    // If unauthorized, trigger re-auth
    if (status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }

    return Promise.reject(apiError);
  },
);

// Generic request helper with type safety
export async function apiRequest<T>(
  endpoint: string,
  options?: ApiRequestOptions,
): Promise<T> {
  const response = await apiClient.request<T>({
    url: endpoint,
    ...options,
  });
  return response.data;
}

// GET helper
export async function get<T>(
  endpoint: string,
  params?: Record<string, unknown>,
  options?: ApiRequestOptions,
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: "GET",
    ...(params !== undefined ? { params } : {}),
  });
}

// POST helper
export async function post<T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions,
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: "POST", data });
}

// Multipart POST helper. Keeps uploads on the same auth, credentials, retry,
// and error-normalization path as JSON API calls.
export async function postForm<T>(
  endpoint: string,
  formData: FormData,
  options?: ApiRequestOptions,
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: "POST",
    data: formData,
  });
}

// PUT helper
export async function put<T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions,
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: "PUT", data });
}

// DELETE helper
export async function del<T>(
  endpoint: string,
  options?: ApiRequestOptions,
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: "DELETE" });
}

// PATCH helper
export async function patch<T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions,
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: "PATCH", data });
}

// Stream helper — uses fetch (not axios) since ReadableStream requires it,
// but reuses the same base URL, auth token, and retry logic as the rest of the client.
const isRetryableStatus = (status: number) =>
  status === 502 || status === 503 || status === 504;

export async function fetchStream(
  endpoint: string,
  data: unknown,
  options: { retryUnsafeMethods?: boolean } = {},
): Promise<Response> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${endpoint}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (_authToken) {
    headers["Authorization"] = `Bearer ${_authToken}`;
  }

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(data),
    });

    if (response.ok) return response;

    // Retry on transient failures with exponential backoff
    if (
      options.retryUnsafeMethods &&
      isRetryableStatus(response.status) &&
      attempt < MAX_RETRIES
    ) {
      await delay(INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt));
      continue;
    }

    // Non-retryable or exhausted retries — throw
    const text = await response.text().catch(() => "Unknown error");
    const message =
      USER_FRIENDLY_MESSAGES[response.status] ??
      `Request failed (${response.status}): ${text}`;

    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }

    throw { detail: message, status: response.status } as ApiError;
  }

  // Should never reach here, but TypeScript needs it
  throw { detail: "Request failed after retries", status: 503 } as ApiError;
}

export default apiClient;
